import { Alert, Button, FormGroup } from "reactstrap";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useAtomValue } from "jotai";
import { isEmpty } from "underscore";
import pluralize from "pluralize";
import dayjs from "dayjs";

import {
  RelatedRecords,
  SharedRecordsField,
  SharedRecordsObject,
  SharedSubviewRecordsInfo,
} from "../../../types/GlobalInterfaces";
import useUpdateSharedRecord from "../../../api/api-hooks/sharedRecords/useUpdateSharedRecord";
import useUpdateExpiration from "../../../api/api-hooks/sharedRecords/useUpdateExpiration";
import useShareRecord from "../../../api/api-hooks/sharedRecords/useShareRecord";
import SharedRecordSubviewsTab from "./SharedRecordSubviewsTab";
import SharedRecordFieldsTab from "./SharedRecordFieldsTab";
import { tenantIDAtom } from "../../../atoms/GlobalAtoms";
import AppNavbar from "../../AppNavbar/AppNavbar";
import CustomToggle from "../Fields/CustomToggle";
import SharedRecordTab from "./SharedRecordTab";
import SyncDetail from "./SyncDetail";

interface Props {
  recordID: string;
  recordModule: string;
  sharedRecord: SharedRecordsField;
  expired: boolean;
  globalAppID?: string;
  sharedRecordRef: any;
  setDisabled: any;
  defaulTab?: Tab;
  fromSubView: boolean;
  onClose?: () => void;
}

const ShareSettingsTabs = [
  { title: "Fields Settings", activeValue: "Fields" },
  { title: "SubViews Settings", activeValue: "SubViews" },
  { title: "Sync Detail", activeValue: "SyncDetail" },
];

type Tab = "Share" | "Fields" | "SubViews" | "SyncDetail";

const initialSharedObject: SharedRecordsObject = {
  RecordID: "",
  ModuleAPIName: "",
  Authentication: "GC User",
  GlobalAppID: "",
  CloudID: "",
  Expires: "",
  Sync: false,
  ReadOnly: false,
  Fields: [],
  RelatedRecords: false,
  RelatedRecordsInfo: {},
  PageID: "",
  ViewID: "",
  SubviewRecords: false,
  SubviewRecordsInfo: {},
  SubviewRecordsLimit: 25,
};

const SharedRecord = ({
  recordID,
  recordModule,
  globalAppID,
  sharedRecord,
  expired,
  sharedRecordRef,
  setDisabled,
  defaulTab = "Share",
  fromSubView,
  onClose,
}: Props) => {
  const [sync, setSync] = useState(false);
  const [readOnly, setReadOnly] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  const tenantID = useAtomValue(tenantIDAtom);
  const { shareRecord, sharing } = useShareRecord();
  const { updateExpiration, updatingExpiration } = useUpdateExpiration();
  const [relatedRecords, setRelatedRecords] = useState<RelatedRecords>({});
  const { pageID, viewID } = useParams();
  const [sharedRecordObject, setSharedRecordObject] =
    useState(initialSharedObject);

  // Updating Shared Record Sync state
  const { updateSharedRecord } = useUpdateSharedRecord();
  const [updateError, setUpdateError] = useState("");
  const [updateSuccess, setUpdateSuccess] = useState(false);
  const [tab, setTab] = useState<Tab>(fromSubView ? "SyncDetail" : defaulTab);
  const [fields, setFields] = useState<string[]>([]);
  const [enableRelatedRecords, toggleRelatedRecords] = useState(false);

  // SubView Record Sharing State
  const [enableSubviewRecords, setEnableSubviewRecords] = useState(false);
  const [subviewRecordInfo, setSubviewRecordInfo] =
    useState<SharedSubviewRecordsInfo>({});
  const [subviewRecordLimit, setSubviewRecordLimit] = useState(25);

  sharedRecordRef.current = handleUpdateSharedRecord;

  const tabs = useMemo(() => {
    const currentDate = new Date();
    const expiresDate = new Date(sharedRecord?.Expires);
    let basicTabs = [{ title: "Share Settings", activeValue: "Share" }];

    if ((sharedRecord && sharedRecord?.CloudID !== tenantID) || fromSubView)
      return [{ title: "Sync Detail", activeValue: "SyncDetail" }];

    if (
      (currentDate > expiresDate || !sharedRecord?.Expires) &&
      !sharedRecord?.RecordID
    ) {
      return basicTabs;
    }

    if (!sharedRecord?.Sync) {
      return basicTabs.concat({
        title: "Sync Detail",
        activeValue: "SyncDetail",
      });
    }

    return basicTabs.concat(ShareSettingsTabs);
  }, [sharedRecord?.Expires]);

  const handleTabChange = (val: "Share" | "Fields") => {
    setTab(val);
  };

  useEffect(() => {
    setDisabled(
      sync === (sharedRecordObject?.Sync ?? false) &&
        readOnly === (sharedRecordObject?.ReadOnly ?? false) &&
        JSON.stringify(fields) ===
          JSON.stringify(sharedRecordObject.Fields ?? []) &&
        enableRelatedRecords ===
          (sharedRecordObject?.RelatedRecords ?? false) &&
        JSON.stringify(relatedRecords) ===
          JSON.stringify(sharedRecordObject?.RelatedRecordsInfo) &&
        !sharedRecordObject?.Authentication &&
        sharedRecordObject?.Authentication === sharedRecord.Authentication
    ) ||
      (sharedRecordObject?.Authentication === "Password" &&
        (!sharedRecordObject.Password ||
          sharedRecord?.Password === sharedRecordObject.Password));
  }, [
    sync,
    readOnly,
    fields,
    enableRelatedRecords,
    relatedRecords,
    sharedRecordObject?.Authentication,
    sharedRecordObject?.Password,
  ]);

  useEffect(() => {
    if (!sharedRecord) return;
    const currentDate = new Date();
    const expiresDate = new Date(sharedRecord?.Expires);

    if (currentDate > expiresDate) {
      sharedRecord.Expires = "";
    }

    setSharedRecordObject(sharedRecord);

    setReadOnly(sharedRecord?.ReadOnly ?? false);
    setFields(sharedRecord?.Fields ?? []);
    toggleRelatedRecords(sharedRecord?.RelatedRecords ?? false);
    setRelatedRecords(sharedRecord?.RelatedRecordsInfo ?? {});
    setSync(sharedRecord?.Sync || false);
    setEnableSubviewRecords(sharedRecord?.SubviewRecords ?? false);
    setSubviewRecordInfo(sharedRecord?.SubviewRecordsInfo ?? {});
    setSubviewRecordLimit(sharedRecord?.SubviewRecordsLimit ?? 25);
  }, [sharedRecord?.Sync]);

  // Toggle for updating "Sync" option
  const SyncToggle = (
    <CustomToggle
      title="Sync Between Clouds"
      description="When enabled, this feature automatically synchronizes updates to the record across multiple connected clouds, ensuring seamless and efficient data consistency."
      value={sync}
      handleChange={(e: any) => setSync(e.target.checked)}
    />
  );

  const ReadOnlyToggle = (
    <CustomToggle
      title="Read Only"
      description="When enabled, the suscribers can only receive updates from the owner."
      value={readOnly}
      handleChange={(e: any) => setReadOnly(e.target.checked)}
    />
  );

  // Shared Record settings - for existing shared records
  const ShareSettings = (
    <>
      <hr />
      {SyncToggle}
      {sync && ReadOnlyToggle}
    </>
  );

  // Shared Record statistics - For existing shared records
  const ShareStats = (
    <>
      <hr />
      <h4>Shared Record Stats</h4>
      {sharedRecord && (
        <>
          This record has been added by{" "}
          {(sharedRecord?.CloudsList?.length ?? 1) - 1}{" "}
          {pluralize("cloud", (sharedRecord?.CloudsList?.length ?? 1) - 1)}
        </>
      )}
    </>
  );

  const SharedRelatedRecords = (
    <div className="mt-4">
      <CustomToggle
        title="Share Referenced Records"
        description="Select references and their respective fields which you want to share."
        value={enableRelatedRecords}
        handleChange={(e: any) => toggleRelatedRecords(e.target.checked)}
      />
    </div>
  );

  return (
    <>
      {updateSuccess && (
        <Alert className="p-2 d-flex align-items-center">
          <i className="mdi mdi-check-circle text-lg ml-1 mr-3" />
          Successfully updated shared record settings!
        </Alert>
      )}
      {updateError && <Alert color="danger">{updateError}</Alert>}
      <AppNavbar tabs={tabs} activeTab={tab} onSelect={handleTabChange} />
      {tab === "Share" && (
        <SharedRecordTab
          //@ts-expect-error
          existingSharedRecord={sharedRecord}
          sharedRecordObject={sharedRecordObject}
          expired={expired}
          SyncToggle={SyncToggle}
          ReadOnlyToggle={sync ? ReadOnlyToggle : undefined}
          ShareSettings={ShareSettings}
          ShareStats={ShareStats}
          handleUnshareRecord={handleUnshareRecord}
          handleShareRecord={handleShareRecord}
          error={error}
          loadingState={{ updatingExpiration, sharing }}
          success={success}
          onChange={(payload) =>
            setSharedRecordObject((prev) => ({
              ...prev,
              ...payload,
            }))
          }
        />
      )}
      {tab === "Fields" && sharedRecord && (
        <>
          <FormGroup className="mt-3">
            <h4 className="mb-0">Shared Fields</h4>
            <div className="">
              <small>Select fields to be shared from this record.</small>
            </div>
          </FormGroup>
          <SharedRecordFieldsTab
            moduleAPIName={recordModule}
            fields={fields}
            setFields={setFields}
            relatedRecords={relatedRecords}
            setRelatedRecords={setRelatedRecords}
            RelatedRecordsHeader={SharedRelatedRecords}
            useRelatedRecords={enableRelatedRecords}
          />
        </>
      )}
      {tab === "SubViews" && sharedRecord && (
        <>
          {sync ? (
            <SharedRecordSubviewsTab
              pageID={pageID}
              viewID={viewID}
              enableSubviewRecords={enableSubviewRecords}
              setEnableSubviewRecords={setEnableSubviewRecords}
              subviewRecordInfo={subviewRecordInfo}
              setSubviewRecordInfo={setSubviewRecordInfo}
              subviewRecordLimit={subviewRecordLimit}
              setSubviewRecordLimit={setSubviewRecordLimit}
            />
          ) : (
            <Alert className="mt-4 font-weight-500" color="warning">
              You must enable the Sync option to be able to share records from
              your SubViews
              <Button
                size="sm"
                className="d-flex align-items-center mt-1"
                onClick={() => setTab("Share")}
              >
                <i className="mdi mdi-arrow-left mr-2 text-sm" />
                Go Back
              </Button>
            </Alert>
          )}
        </>
      )}
      {tab === "SyncDetail" && (
        <SyncDetail
          sharedRecord={sharedRecord}
          tenantID={tenantID || ""}
          recordID={recordID}
          onClose={onClose}
        />
      )}
      {tab !== "Share" && !sharedRecord && (
        <Alert className="mt-4 font-weight-500" color="warning">
          You must share the record first before configuring additional
          settings.
          <Button
            size="sm"
            className="d-flex align-items-center mt-1"
            onClick={() => setTab("Share")}
          >
            <i className="mdi mdi-arrow-left mr-2 text-sm" />
            Go Back
          </Button>
        </Alert>
      )}
    </>
  );

  function resetUpdateState() {
    setUpdateSuccess(false);
    setUpdateError("");
  }

  // Create Shared Record
  function handleShareRecord() {
    if (
      !globalAppID ||
      !tenantID ||
      ((!viewID || !pageID) && !sharedRecordObject)
    )
      return;

    setSuccess("");
    setError("");
    resetUpdateState();

    const expiresDate =
      dayjs()
        .add(parseInt(sharedRecordObject?.Expires || "0"), "minutes")
        .toISOString() ?? "";

    if (sharedRecord) {
      return updateExpiration({
        recordID: recordID,
        newExpiration: expiresDate,
        viewID: viewID ?? sharedRecordObject?.ViewID,
        pageID: pageID ?? sharedRecordObject?.PageID,
        onSuccess: () =>
          setSharedRecordObject((prev) => ({ ...prev, Expires: expiresDate })),
        onError: (err) => setError(err),
      });
    }

    const payload: SharedRecordsObject = {
      RecordID: recordID,
      ModuleAPIName: recordModule,
      GlobalAppID: globalAppID,
      CloudID: tenantID,
      Expires: expiresDate,
      Authentication: sharedRecordObject?.Authentication,
      ...(sharedRecordObject?.Authentication === "Password" && {
        Password: sharedRecordObject?.Password,
      }),
      Sync: sync,
      ReadOnly: !sync ? false : readOnly,
      Fields: fields,
      RelatedRecords: enableRelatedRecords,
      RelatedRecordsInfo: relatedRecords,
      ViewID: viewID ?? sharedRecordObject?.ViewID,
      PageID: pageID ?? sharedRecordObject?.PageID,
    };

    shareRecord({
      payload,
      onSuccess: () => {
        setSuccess(
          "Sucessfully shared this record! Copy the link above to share it with others."
        );
      },
      onError: (err: string) => setError(err),
    });
  }

  function handleUnshareRecord() {
    if ((!viewID || !pageID) && !sharedRecordObject) return;

    resetUpdateState();
    updateExpiration({
      recordID: recordID,
      newExpiration: null,
      pageID: pageID ?? sharedRecordObject?.PageID,
      viewID: viewID ?? sharedRecordObject?.ViewID,
      onSuccess: () =>
        setSharedRecordObject((prev) => ({ ...prev, Expires: "" })),
    });
  }

  // Update Shared Record
  function handleUpdateSharedRecord() {
    setUpdateError("");
    setUpdateSuccess(false);

    let payload: any = {
      ...(sync !== (sharedRecordObject?.Sync ?? false) && {
        Sync: sync,
        ...(sync === false && {
          ReadOnly: false,
          SubviewRecords: false,
        }),
      }),
      ...(JSON.stringify(fields) !==
        JSON.stringify(sharedRecordObject?.Fields) && { Fields: fields }),
      ...(enableRelatedRecords !==
        (sharedRecordObject?.RelatedRecords ?? false) && {
        RelatedRecords: enableRelatedRecords,
      }),
      ...(JSON.stringify(relatedRecords) !==
        JSON.stringify(sharedRecordObject?.RelatedRecordsInfo) && {
        RelatedRecordsInfo: relatedRecords,
      }),
      ...(sharedRecordObject?.Authentication !==
        sharedRecord.Authentication && {
        Authentication: sharedRecordObject?.Authentication,
        ...(sharedRecordObject?.Authentication === "Password"
          ? { Password: sharedRecordObject?.Password }
          : {
              Password: "",
            }),
      }),
      ...(readOnly !== (sharedRecordObject?.ReadOnly ?? false) && {
        ReadOnly: readOnly,
      }),
      ...(JSON.stringify(subviewRecordInfo) !==
        JSON.stringify(sharedRecordObject?.SubviewRecordsInfo) && {
        SubviewRecordsInfo: subviewRecordInfo,
      }),
      ...(enableSubviewRecords !==
        (sharedRecordObject?.SubviewRecords ?? false) && {
        SubviewRecords: enableSubviewRecords,
        ...(!enableSubviewRecords && { SubviewRecordsInfo: {} }),
      }),
      ...(subviewRecordLimit !== sharedRecordObject?.SubviewRecordsLimit && {
        SubviewRecordsLimit: subviewRecordLimit,
      }),
      PageID: pageID,
      ViewID: viewID,
      ...(sync === false && {
        SubviewRecordsInfo: {},
      }),
    };

    if (isEmpty(payload)) return;

    updateSharedRecord({
      recordID: recordID,
      payload,
      onError: setUpdateError,
      onSuccess: () => {
        setUpdateSuccess(true);
        setSharedRecordObject((prev) => ({ ...prev, ...payload }));
      },
    });
  }
};

export default SharedRecord;
